import React, { useCallback, useMemo, useRef } from 'react';

import { useTranslation } from '@modernloop/i18n';
import { PublicError } from '@modernloop/shared/helper-components';
import { ArrowUpIcon } from '@modernloop/shared/icons';
import {
  getAgentSparkleIconDataUri,
  getPrimaryAccentBackground,
  getPrimaryAccentContrastText,
} from '@modernloop/shared/theme';
import { CircularProgress, IconButton, Stack, Theme, useTheme } from '@mui/material';
import Mention from '@tiptap/extension-mention';
import Placeholder from '@tiptap/extension-placeholder';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';

import {
  SourceType,
  useActivityNoteCreateWithContentMutation,
  useMentionEmployeeSearchLazyQuery,
} from 'src/generated/mloop-graphql';

import { serializeActivityLogContent } from 'src/entities/ActivityLog/Helpers/activityLogMentions';
import { useAiAgentMentionLabel } from 'src/entities/ActivityLog/Helpers/useAiAgentLabel';

import { DictationMicrophoneIcon, DictationStopIcon } from './DictationIcons';
import type { MentionSuggestionItem } from './MentionSuggestionList';
import { createMentionSuggestion } from './suggestion';
import { useMentionEditorDictation } from './useMentionEditorDictation';

type Props = {
  sourceId: string;
  sourceType: SourceType;
  onSubmit: (note: string) => void;
  onChange: (note: string) => void;
  aiEnabled?: boolean;
};

const MentionEditor: React.FC<Props> = ({ sourceId, sourceType, onSubmit, onChange, aiEnabled = false }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [activityNoteCreateMutation, { loading, error }] = useActivityNoteCreateWithContentMutation();
  const aiAgentLabel = useAiAgentMentionLabel();
  const [searchEmployees] = useMentionEmployeeSearchLazyQuery();

  const getEmployees = useCallback(
    async (query: string): Promise<MentionSuggestionItem[]> => {
      try {
        const { data } = await searchEmployees({
          variables: { input: { search: query || '', pageInput: { limit: 10 } } },
        });
        return (
          data?.employeeSearch?.items?.map((emp) => ({
            id: emp.id,
            label: emp.fullName || emp.email || 'Unknown',
            avatarUrl: emp.internalProfilePictureUrl || undefined,
          })) ?? []
        );
      } catch {
        return [];
      }
    },
    [searchEmployees]
  );

  const mentionSuggestionOpenRef = useRef(false);
  const onMentionSuggestionActiveChange = useCallback((active: boolean) => {
    mentionSuggestionOpenRef.current = active;
  }, []);

  const suggestion = useMemo(
    () =>
      createMentionSuggestion(getEmployees, aiEnabled, aiAgentLabel, {
        onActiveChange: onMentionSuggestionActiveChange,
      }),
    [getEmployees, aiEnabled, aiAgentLabel, onMentionSuggestionActiveChange]
  );
  const handleSubmitRef = useRef<() => void>(() => {});

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // Disable features we don't need — keep it simple like a chat input
        heading: false,
        codeBlock: false,
        blockquote: false,
        bulletList: false,
        orderedList: false,
        horizontalRule: false,
      }),
      Placeholder.configure({
        placeholder: 'Add a comment, @mention users or AI Agent...',
      }),
      Mention.configure({
        HTMLAttributes: {
          class: 'mention',
        },
        suggestion,
      }),
    ],
    editorProps: {
      attributes: {
        style:
          "outline: none; min-height: 36px; max-height: 120px; overflow-y: auto; font-family: 'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif; font-size: 13px; line-height: 18px; padding: 12px;",
      },
      handleKeyDown: (view, event) => {
        const isPlainEnter = event.key === 'Enter' && !event.shiftKey && !event.ctrlKey && !event.metaKey;
        const isModEnter = event.key === 'Enter' && (event.ctrlKey || event.metaKey);

        if (isPlainEnter && mentionSuggestionOpenRef.current) {
          return false;
        }

        if (isPlainEnter) {
          const plainText = (view.dom.textContent ?? '').trim();
          if (plainText) {
            event.preventDefault();
            handleSubmitRef.current();
            return true;
          }
          return false;
        }

        if (isModEnter) {
          event.preventDefault();
          handleSubmitRef.current();
          return true;
        }

        return false;
      },
    },
    onUpdate: ({ editor: e }) => {
      onChange(e.getText());
    },
  });
  const { isDictationSupported, isDictating, isStoppingDictation, toggleDictation } = useMentionEditorDictation(editor);

  const handleSubmit = useCallback(() => {
    if (!editor) {
      return;
    }

    const noteJson = editor.getJSON();
    const { markdown, plaintext } = serializeActivityLogContent(noteJson, aiAgentLabel);
    const trimmedPlaintext = plaintext.trim();
    const trimmedMarkdown = markdown.trim();

    if (!trimmedPlaintext) {
      return;
    }

    activityNoteCreateMutation({
      variables: {
        input: {
          note: trimmedPlaintext,
          content: {
            markdown: trimmedMarkdown,
            plaintext: trimmedPlaintext,
            formatVersion: 2,
            editorStateJson: JSON.stringify(noteJson),
          },
          sourceId,
          sourceType,
        },
      },
    })
      .then(() => {
        editor.commands.clearContent();
        onSubmit(trimmedPlaintext);
        return undefined;
      })
      .catch(() => {
        // Error is handled by the mutation state
      });
  }, [aiAgentLabel, editor, activityNoteCreateMutation, sourceId, sourceType, onSubmit]);
  handleSubmitRef.current = handleSubmit;
  const hasContent = editor?.getText()?.trim();

  return (
    <Stack direction="column" spacing={0.5}>
      <Stack
        sx={{
          border: 1,
          borderColor: 'divider',
          borderRadius: '12px',
          boxShadow: (theme: Theme) => theme.shadows[2],
          '&:focus-within': {
            borderColor: 'primary.main',
          },
          // Style the mention chips inside the editor
          '& .mention': {
            borderRadius: '4px',
            px: '4px',
            py: '1px',
            fontWeight: 600,
            fontSize: '13px',
            boxDecorationBreak: 'clone',
            WebkitBoxDecorationBreak: 'clone',
          },
          '& .mention[data-id="ai-agent"]': {
            background: (theme: Theme) => getPrimaryAccentBackground(theme),
            color: (theme: Theme) => getPrimaryAccentContrastText(theme),
            pl: '22px',
            position: 'relative',
          },
          '& .mention[data-id="ai-agent"]::before': {
            content: '""',
            position: 'absolute',
            left: '5px',
            top: '50%',
            transform: 'translateY(-50%)',
            width: '12px',
            height: '12px',
            backgroundImage: `url("${getAgentSparkleIconDataUri(theme.palette.primary.contrastText)}")`,
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
            backgroundSize: '12px 12px',
          },
          '& .mention:not([data-id="ai-agent"])': {
            background: (theme: Theme) => theme.palette.primary.tint,
            color: 'primary.dark',
          },
          // Placeholder styling
          '& .tiptap p.is-editor-empty:first-child::before': {
            color: 'text.disabled',
            content: 'attr(data-placeholder)',
            float: 'left',
            height: 0,
            pointerEvents: 'none',
          },
        }}
      >
        <EditorContent editor={editor} />
        <Stack direction="row" justifyContent="space-between" px={1} pb={1}>
          <Stack direction="row" spacing={0.75}>
            {isDictationSupported ? (
              <IconButton
                size="small"
                onClick={toggleDictation}
                disabled={isStoppingDictation}
                aria-label={
                  isDictating
                    ? t('hiring-dashboard-candidates:candidates.activityLog.stopDictation', {
                        defaultValue: 'Stop dictation',
                      })
                    : t('hiring-dashboard-candidates:candidates.activityLog.startDictation', {
                        defaultValue: 'Start dictation',
                      })
                }
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: '20px',
                  color: isDictating ? 'common.white' : 'text.secondary',
                  bgcolor: isDictating ? 'error.main' : 'action.hover',
                  '&:hover': {
                    bgcolor: isDictating ? 'error.dark' : 'action.selected',
                  },
                }}
              >
                {isDictating ? <DictationStopIcon fontSize="small" /> : <DictationMicrophoneIcon fontSize="small" />}
              </IconButton>
            ) : null}
          </Stack>
          {loading ? (
            <CircularProgress size={16} />
          ) : (
            <IconButton
              size="small"
              onClick={handleSubmit}
              disabled={!hasContent}
              sx={{
                width: 28,
                height: 28,
                bgcolor: hasContent ? 'primary.main' : 'action.disabledBackground',
                color: hasContent ? 'primary.contrastText' : 'text.disabled',
                borderRadius: '20px',
                '&:hover': {
                  bgcolor: hasContent ? 'primary.dark' : undefined,
                },
                '&.Mui-disabled': {
                  color: 'text.disabled',
                },
              }}
            >
              <ArrowUpIcon fontSize="small" />
            </IconButton>
          )}
        </Stack>
      </Stack>
      {error ? <PublicError error={error} /> : null}
    </Stack>
  );
};

export default MentionEditor;
